import axios, { AxiosInstance, AxiosResponse } from 'axios';
import path from 'path';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import async from 'async';
import _ from 'lodash';
import { WriteStream } from 'fs-extra';
import systemClock from '@/common/SystemClock';
import smh, { SMHSDK } from '@/renderer/scripts/smh';
import getRequest from '@/renderer/scripts/httpRequest';
import { CommonLoader, DownloadConfig } from '@/renderer/scripts/utils/CommonLoader';
import { isExpiredInPrivate } from '@/renderer/scripts/utils/download/utils';
import { IS_BROWSER } from '@/common/constant/platform';
import { TaskStatus } from '@/renderer/scripts/utils/TaskManager/types';
import { transformErrorForSerialization } from '@/common/tools';
import { getCrc64Promise } from '../upload/utils';
import { DownloadFileInfo, PartsInfo } from './types';

/**
 * 下载任务状态流转
 * 1、简单下载
 * 任务开始(preparing状态) -> 本地临时文件创建后(改为running状态) - 任务结束
 * 2、高级下载
 * 任务开始(preparing状态) -> 本地临时文件创建后(改为running状态) - 结束前进行crc64对比(改为preparing) - 任务结束
 */

dayjs.extend(duration);

let fs: typeof import('fs');
if (!IS_BROWSER) {
  fs = (window.parent as any).require('fs') as typeof import('fs');
}

export default class Downloader extends CommonLoader<DownloadFileInfo> {
  protected cosRequest: AxiosInstance;
  public info: DownloadFileInfo;

  private chunkSize: number = DownloadConfig.ChunkSize; // 分片下载时真实分片大小

  private startTimeStamp = Date.now();

  protected smh!: SMHSDK;

  constructor(
    info: DownloadFileInfo = {
      name: '',
      downloadTarget: '',
      contentType: '',
      type: '',
      size: 0,
      eTag: '',
      crc64: '',
      newCrc64: '',
      filePath: '',
      saveFileRelativePath: '',
      saveRootFolderName: '',
      savePath: '',
      savePathTmp: '',
      restart: false,
      rename: true,
      // 默认自动重命名
      routerName: '',
    },
    smhParam?: SMHSDK,
  ) {
    super('download', info);
    this.info = info;
    if (smhParam) {
      this.smh = smhParam;
    } else {
      this.smh = smh;
    }
    this.cosRequest = getRequest({
      maxBodyLength: DownloadConfig.COSRequestMaxBodyLength,
      logPrefix: 'COS download',
    });

    this.on('status_changed', val => {
      // 若下载任务被 取消 | 暂停 ，则取消请求的发送
      if ([TaskStatus.canceled, TaskStatus.paused].includes(val)) {
        this.cancelRequestSource.cancel('canceled');
        this.cancelRequestSource = axios.CancelToken.source();
        if (this.streamGroup) {
          this.streamGroup.destroyAllStreams();
        }
      }
      /**
       * 分块下载: 若下载任务被 取消，则删除本地缓存
       * 简单下载: 若下载任务被 取消 | 出错 | 暂停，则删除本地缓存
       * */
      if (this.isSimpleDownload()) {
        if ([TaskStatus.canceled, TaskStatus.error, TaskStatus.paused].includes(val) && this.info.savePathTmp) {
          Downloader.deleteFile(this.info.savePathTmp);
        }
      } else {
        if ([TaskStatus.canceled].includes(val) && this.info.savePathTmp) {
          Downloader.deleteFile(this.info.savePathTmp);
        }
      }
    });
  }

  /**
   * @description 删除本地文件
   * @private
   * @static
   * @param {string} [path]
   * @return {*}
   * @memberof Downloader
   */
  private static async deleteFile(path?: string) {
    if (!path) return;
    if (fs.existsSync(path)) {
      try {
        fs.unlinkSync(path);
      } catch (error) {
        logger.ui.error(`[Downloader] deleteFile: ${path} error`, error);
      }
    }
  }
  private async getSimpleDownloadData(res: AxiosResponse<any, any>) {
    // FIX: 非200状态码已经在 httprequest里进行处理并reject了，绝对不会进入到then里来，因此这里只需要考虑有2xx返回的情况
    let loaded = 0;
    this.emitProgress(0, { immediately: true });
    const rs = res.data;

    // 文件的真实大小从响应头中取得(比如图片加水印后下载真实大小不等于远端的大小)
    if (rs.headers['content-length']) {
      this.info.size = +rs.headers['content-length'];
    }

    let writeDataTime = 0;
    let timeoutFinish = false;
    const clean = (error: { code: string; interface: string }) => {
      logger.ui.error(`[Downloader] clean ${this.info.name} ${error.code} ${this.status}`);
      timeoutFinish = true;
      if (writeStream) writeStream.close();

      // 手动暂停时不用改成error
      if (this.status === TaskStatus.paused && error.code === 'NetWorkAborted') return;
      this.setStatus(TaskStatus.error);
      this.emit('error', { ...error, downloadType: 'simple' });
    };
    let writeCheckTimer: number | undefined = window.setInterval(() => {
      // 定时检测是否有数据写入
      const span = Date.now() - writeDataTime;
      if (span > DownloadConfig.MaxWriteDataTimeout && !timeoutFinish) {
        clean({ code: 'NetworkError', interface: 'NetworkError' });
      }
    }, DownloadConfig.MaxWriteDataTimeout);
    let writeStream: WriteStream;
    let currentStreamBeanId = '';
    if (!this.streamGroup) {
      const fsWriteStream = fs.createWriteStream(this.info.savePathTmp as string);
      writeStream = rs.pipe(fsWriteStream);
    } else {
      const { destStream, id } = this.streamGroup.createLimitStream({
        type: 'download',
        responseStream: rs,
        path: this.info.savePathTmp as string,
      });
      currentStreamBeanId = id;
      writeStream = destStream as any as WriteStream;
    }
    rs.on('aborted', (error: any) => {
      clean({ code: error?.code || 'NetWorkAborted', interface: 'NetworkError' });
    });
    writeStream.on('error', async (error: any) => {
      window.clearInterval(writeCheckTimer);
      writeCheckTimer = undefined;
      writeStream.destroy(new Error(`destroy writeStream cause error`));
      logger.ui.error(`[Downloader] [${this.info.name}] writeStream error`, error, this.status);
      this.streamGroup && this.streamGroup.destroyStreamByBeanId(currentStreamBeanId);
      this.setStatus(TaskStatus.error);
      this.emit('error', {
        ...error,
        interface: 'Downloader.getObject',
        downloadType: 'simple',
      });
    });

    rs.on('data', (chunk: any) => {
      if (this.status === 'running') {
        loaded += chunk.length;
        writeDataTime = Date.now();
        this.emitProgress(loaded);
      }
    });
    writeStream.on('finish', async () => {
      this.streamGroup && this.streamGroup.destroyStreamByBeanId(currentStreamBeanId);
      window.clearInterval(writeCheckTimer);
      writeStream.close();
      if (timeoutFinish) {
        logger.ui.info(`[Downloader] [${this.info.name}] simpleDownload finish have error`);
        return;
      }
      if (this.status === 'running') {
        try {
          await this.finishDownload();
        } catch (error: any) {
          this.setStatus(TaskStatus.error);
          this.emit('error', { ...error, interface: 'Downloader.getObject', downloadType: 'simple' });
          return;
        }

        if (!this.isRunning()) return;
        this.setStatus(TaskStatus.success);
        this.emit('success', { downloadType: 'simple' });
      }
    });
    this.once('status_changed', val => {
      // 若下载任务被 取消|删除|暂停，及时关闭写入并删除本地文件（只需要监听一次即可）
      if ([TaskStatus.canceled, TaskStatus.error, TaskStatus.paused].includes(val)) {
        writeCheckTimer && window.clearInterval(writeCheckTimer);
        timeoutFinish = false;
        if (writeStream) writeStream.close();
      }
    });
  }

  /**
   * @description 简单下载
   * @private
   * @memberof Downloader
   */
  private async simpleDownload() {
    if (!this.isRunning()) return;

    const downloadFile = async (retry: number) => {
      try {
        await this.getAvailablePath('simple');
        this.setStatus(TaskStatus.running);
        const { cosUrl, contentType } = this.info;
        this.cosRequest({
          url: cosUrl,
          method: 'GET',
          headers: {
            'Content-Type': contentType,
          },
          timeout: DownloadConfig.RequestTimeout,
          responseType: 'stream',
          cancelToken: this.cancelRequestSource.token,
        })
          .then(async res => {
            await this.getSimpleDownloadData(res);
          })
          .catch(async error => {
            logger.ui.error(
              `[Downloader] [${this.info.name}] simpleDownload error`,
              JSON.stringify(transformErrorForSerialization(error)),
            );
            let isExpired = false;
            if (error.status === 403) {
              // 公有云超时判断 || 私有云超时判断
              isExpired =
                error.message === 'Request has expired' || // 公有云判断超时
                error.code === 'RequestTimeTooSkewed' || // 私有云超时判断
                isExpiredInPrivate(cosUrl as string, error.headers?.date); // 私有云超时判断
            }
            // 重试一次：下载到 COS 的签名超时 | 5xx 服务器错误
            if (isExpired || Math.floor(error.status / 100) === 5) {
              if (retry < DownloadConfig.ChunkRetryTimes) {
                if (isExpired) {
                  await this.getDownloadCosUrl();
                }
                downloadFile(retry + 1);
              } else {
                if (error.message === 'canceled') return;

                this.setStatus(TaskStatus.error);
                this.emit('error', { ...error, interface: 'COS.getObject', downloadType: 'simple' });
              }
            } else if (retry < DownloadConfig.ChunkRetryTimes) {
              downloadFile(retry + 1);
            } else {
              if (error.message === 'canceled') return;

              this.setStatus(TaskStatus.error);
              this.emit('error', { ...error, interface: 'COS.getObject', downloadType: 'simple' });
            }
          });
      } catch (error: any) {
        logger.ui.error(`[Donwloader] [${this.info.name}] simpleDownload downloadFile error`, error);
        if (error.message === 'canceled') return;

        this.setStatus(TaskStatus.error);
        this.emit('error', { ...error, interface: 'COS.getObject', downloadType: 'simple' });
      }
    };
    downloadFile(0);
  }

  /**
   * 根据文件本体，自动计算文件分块大小
   * @param FileSize
   * @param ChunkSize
   * @private
   */
  private _calcAutoChunkSize(FileSize: number, ChunkSize: number): number {
    const SIZE = [1, 2, 4, 8, 16, 32, 64, 128, 256, 512, 1024, 1024 * 2, 1024 * 4, 1024 * 5];
    let AutoChunkSize = 1024 * 1024;
    for (let i = 0; i < SIZE.length; i++) {
      AutoChunkSize = SIZE[i] * 1024 * 1024;
      if (FileSize / AutoChunkSize <= DownloadConfig.MaxPartNumber) break;
    }
    if (typeof window.downloadChunkSize !== 'undefined') {
      if (FileSize / window.downloadChunkSize > DownloadConfig.MaxPartNumber) {
        /* i18n-ignore*/
        throw new Error('debug 下载设置的 chunkSize 过小，导致分块数量超过 1w块，请检查');
      }
      return window.downloadChunkSize;
    }
    return Math.max(ChunkSize, AutoChunkSize);
  }

  /**
   * @description 分块下载
   * @memberof Downloader
   */
  private async advanceDownload() {
    const downloadType = 'advanced';
    if (!this.isRunning()) return;

    const { ChunkSize } = DownloadConfig;
    const FileSize = this.info.size;
    if (!this.chunkSize) {
      this.chunkSize = this._calcAutoChunkSize(FileSize, ChunkSize);
    }
    const sliceCount = Math.ceil(FileSize / this.chunkSize);
    const handlerError = (error: MsgError) => {
      this.setStatus(TaskStatus.error);
      this.emit('error', { ...error, downloadType });
    };

    // 下载所有分片
    try {
      await this.getAvailablePath('advanced');
      if (!this.isRunning()) return;
      // getAvailablePath之后强行更新一下任务状态 保证更新进DB
      this.info.downloadParts = await this.getDownloadPartInfo();
      // 校验每个分块的crc64 是否发生了改变
      await this.checkLocalPartCrc64();
      if (!this.isRunning()) return;

      this.setStatus(TaskStatus.running);
      await this.downloadAllParts(sliceCount);
      if (!this.isRunning()) return;
      this.setStatus(TaskStatus.preparing);
      await this.finishDownload();
      if (!this.isRunning()) return;
      this.setStatus(TaskStatus.success);
      this.emit('success', { downloadType });
    } catch (error: MsgError) {
      if (error.message === 'canceled') return;
      logger.ui.error(`[Downloader] [${this.info.name}] advanceDownload error`, error);
      handlerError(error);
    }
  }

  /**
   * @description 检查本地part文件是否被修改过
   * @private
   * @return {*}
   * @memberof Downloader
   */
  private checkLocalPartCrc64(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      // 1、过滤出本地已经写好的分块
      const hasCrc64PartList: PartsInfo[] = this.info.downloadParts?.filter(part => part.partCrc64) as PartsInfo[];
      if (hasCrc64PartList.length === 0) {
        return resolve();
      }
      // 2、重新计算本地分块的crc64并对比
      try {
        await async.forEachLimit(hasCrc64PartList, 5, (part, next) => {
          getCrc64Promise(this.info.savePathTmp as string, { start: part.Start, end: part.End })
            .then(res => {
              const newCrc64 = res;
              // 本地分块被修改时 标记该分块应该重新下载
              if (newCrc64 !== part.partCrc64) {
                part.Downloaded = false;
                part.partCrc64 = '';
              }
              next();
            })
            .catch(err => {
              next(err);
            });
        });
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * @description 下载前准备本地文件夹
   * @private
   * @return {*}
   * @memberof Downloader
   */
  private prepareLocalFolder(dirPath: string) {
    if (!this.isRunning()) return;

    // 要保存的本地文件夹路径不存在时需要创建
    if (!fs.existsSync(dirPath)) {
      const { sep } = path;
      let pathtmp!: string;
      dirPath.split(sep).forEach(dirname => {
        if (pathtmp) {
          pathtmp = path.join(pathtmp, dirname);
        } else {
          // 若第一个dirname的值为空，则赋值为分隔符
          if (dirname) {
            pathtmp = dirname;
          } else {
            pathtmp = sep;
          }
        }
        if (!fs.existsSync(pathtmp)) {
          fs.mkdirSync(pathtmp);
        }
      });
    }
  }

  /**
   * @description 获取可用的保存路径，便于文件流写入
   * @private
   * @memberof Downloader
   */
  private async getAvailablePath(downloadType: 'simple' | 'advanced') {
    const { size, downloadTarget, saveFileRelativePath, name } = this.info;

    // 当有本地缓存路径信息并且存在时，认为空文件已经创建好了
    if (this.info.savePath && this.info.savePathTmp && fs.existsSync(this.info.savePathTmp)) {
      return;
    }
    const ext = path.extname(name);
    const fileNameWithoutExt = saveFileRelativePath.slice(0, -ext.length);
    let savePath = path.join(downloadTarget, saveFileRelativePath);
    let savePathTmp = `${savePath}.tced.part`;

    // 外部已经设置临时存储路径优先使用外部临时路径
    if (this.info.savePathTmp && this.info.savePathTmp.length > 0) {
      savePathTmp = this.info.savePathTmp;
    }

    if (('rename' in this.info && this.info.rename) || !('rename' in this.info)) {
      let i = 1;
      while (i <= 1000000 && (fs.existsSync(savePath) || fs.existsSync(savePathTmp))) {
        savePath = path.resolve(downloadTarget, `${fileNameWithoutExt} (${i})${ext}`);
        savePathTmp = `${savePath}.tced.part`;
        i++;
      }
    }
    this.info.savePath = savePath;
    this.info.savePathTmp = savePathTmp;
    this.info.downloadParts = [];
    const emptyFile = size === 0;
    const writeFilePath = emptyFile ? savePath : savePathTmp;

    // 要保存的本地文件夹路径不存在时需要创建
    const dirPath = path.dirname(this.info.savePath);
    this.prepareLocalFolder(dirPath);
    if (!fs.existsSync(writeFilePath)) {
      try {
        fs.writeFileSync(writeFilePath, '');
      } catch (error) {
        logger.ui.error(`[Downloader] [${this.info.name}] create empty file error`, error);
        return Promise.reject(error);
      }
    }
  }

  /**
   * @description 获取下载分片信息
   * @private
   * @param {number} sliceCount
   * @memberof Downloader
   */
  private async getDownloadPartInfo() {
    const { chunkSize } = this;
    const { size, eTag: ETag, downloadParts, crc64, newCrc64 } = this.info;
    let partList: PartsInfo[] = [];

    // 新建全新的downloadParts
    const createDownloadParts = () => {
      const list = [];
      let Start = 0;
      let PartNumber = 1;
      while (Start < size) {
        // 不在完成的分块map中，则加入分块数组
        list.push({
          Start,
          End: Start + chunkSize - 1,
          PartNumber,
          ETag,
          partCrc64: '',
          Downloaded: false,
        });
        Start += chunkSize;
        PartNumber += 1;
      }
      list[list.length - 1].End = size - 1;
      return list;
    };

    // 如果是全新的任务，则新建分块列表
    if (!downloadParts || downloadParts.length === 0) {
      partList = createDownloadParts();
      return partList;
    }

    // 如果是暂停后续传的任务，则获取本地临时文件状态
    if (downloadParts) {
      partList = downloadParts;
    }

    // 如果最新的 CRC64ECMA 校验值，和初始的crc64对比，看线上文件是否有变化
    if (newCrc64 !== crc64) {
      return Promise.reject({
        interface: 'SMH.remoteFileHasChanged',
        code: 'remoteFileHasChanged',
      });
    }

    return partList;
  }

  /**
   * @description 下载所有分片
   * @private
   * @memberof Downloader
   */
  private async downloadAllParts(sliceCount: number) {
    if (!this.isRunning()) return;

    this.setStatus(TaskStatus.running);
    const { downloadParts, size } = this.info;
    const { chunkSize } = this;
    let FinishSize = 0;
    this.emitProgress(0, { immediately: true });
    if (downloadParts?.length) {
      const needDownloadParts = downloadParts.filter(part => {
        if (part.Downloaded) {
          FinishSize += part.PartNumber >= sliceCount ? size % chunkSize || chunkSize : chunkSize;
        }
        return !part.Downloaded || !part.partCrc64;
      });
      logger.ui.info(`[Downloader] [${this.info.name}, init FinishSize = ${FinishSize}`);
      let shouldStopByCancel = false; // 下载已取消时，重试要停下来
      const asyncTimeStamp = this.startTimeStamp; // 记录这次开始的时间戳用于判断mapLimit里面执行的代码是否需要继续执行下去
      await async.mapLimit(needDownloadParts, DownloadConfig.ChunkParallelLimit, (part: PartsInfo, next) => {
        if (!this.isRunning() || shouldStopByCancel || asyncTimeStamp !== this.startTimeStamp) {
          next(new Error('canceled'));
          return;
        }
        const currentSize = Math.min(size, part.PartNumber * chunkSize) - (part.PartNumber - 1) * chunkSize;
        let preAddSize = 0;
        const onProgress = (_loaded: number) => {
          if (this.status !== TaskStatus.running) return;
          FinishSize += _loaded;
          preAddSize += _loaded;
          this.emitProgress(FinishSize);
        };
        this.downloadPart(part, {
          onProgress,
          resetSize: () => {
            FinishSize -= preAddSize;
            preAddSize = 0;
          },
        })
          .then(async () => {
            // 暂停和错误不应该认为下载完成
            if (this.status === TaskStatus.paused || this.status === TaskStatus.error) return;
            FinishSize += currentSize - preAddSize;
            // 下载完成后计算当前块的crc64并记录
            const partCrc64 = await getCrc64Promise(this.info.savePathTmp as string, {
              start: part.Start,
              end: part.End,
            });
            part.partCrc64 = partCrc64;
            part.Downloaded = true;
            // 排查时可打日志 上线后屏蔽 避免日志过多
            // logger.ui.info(
            //   `[Downloader] [${this.info.name}, part=${part.PartNumber}] downloaded success, FinishSize = ${FinishSize}`,
            // );
            this.emitProgress(FinishSize, { immediately: true });
            next();
          })
          .catch(error => {
            if (error?.message === 'canceled') {
              shouldStopByCancel = true;
            }
            FinishSize -= preAddSize;
            this.emitProgress(FinishSize);
            if (this.status === TaskStatus.paused) {
              next(new Error('canceled')); // 暂停立即停掉循环
              return;
            }
            logger.ui.error(`[Downloader] [${this.info.name}] download part error`, part, error);
            next(error);
          });
      });

      logger.ui.info(`[Downloader] [${this.info.name}] downloadAllParts end`);
    }
  }
  private async getDownloadPartData(
    res: AxiosResponse<any, any>,
    done: any,
    part: PartsInfo,
    options: {
      onProgress: (loaded: number) => void;
      resetSize: () => void;
      retryTimes: number;
      startTime: number;
    },
  ) {
    const { onProgress, resetSize } = options;
    // TODO: (后续可删除)非200状态码已经在 httprequest里进行处理并reject了，绝对不会进入到then里来，因此这里只需要考虑有2xx返回的情况
    let writeDataTime = 0;
    let timeoutFinish = false;
    let writeCheckTimer: number | undefined = window.setInterval(() => {
      // 定时检测是否有数据写入
      const span = Date.now() - writeDataTime;
      if (span > DownloadConfig.MaxWriteDataTimeout && !timeoutFinish) {
        timeoutFinish = true;
        const reason = `[Download] ${this.info.name} downloadPart write timeout`;
        cleanAndReStart(reason);
      }
    }, DownloadConfig.MaxWriteDataTimeout);
    // 清理当前进程的stream，并直接开始重试下一个part
    let live = false;
    const cleanAndReStart = (reason: string) => {
      if (live) return;
      live = true;
      window.clearInterval(writeCheckTimer);
      writeCheckTimer = undefined;
      logger.ui.info(`[Downloader] ${this.info.name} partNum:${part.PartNumber} ${reason} ${this.status}`);
      if (IS_BROWSER) {
        if (writeStream) writeStream.close();
      } else {
        this.streamGroup && this.streamGroup.destroyStreamByBeanId(currentStreamBeanId);
      }
      resetSize();
      if (options.retryTimes + 1 <= 5 && this.status !== TaskStatus.paused) {
        /* i18n-ignore*/
        const msg = options.retryTimes + 1 === 5 ? `本次part下载失败` : '';
        logger.ui.info(
          `[Downloader] [downloadPart] ${this.info.name}|start:${options.startTime}|end:${Date.now()}|,partNum:${
            part.PartNumber
          },重试第${options.retryTimes + 1}次,${msg} `,
        );
      }
      done({
        interface: 'NetworkError',
      });
    };
    const filePath = path.join(this.info.savePathTmp as string);
    const rs = res.data;
    let writeStream: WriteStream;
    let currentStreamBeanId = '';
    if (!this.streamGroup) {
      writeStream = fs.createWriteStream(path.join(this.info.savePathTmp as string), {
        start: part.Start,
        flags: 'r+',
      });
      rs.pipe(writeStream);
    } else {
      // uploader 需要进行主动的 destroy
      const { destStream, id } = this.streamGroup.createLimitStream(
        {
          type: 'download',
          responseStream: res.data,
          path: filePath,
        },
        {
          start: part.Start,
          flags: 'r+',
        },
      );

      writeStream = destStream as any as WriteStream;
      currentStreamBeanId = id;
    }
    writeStream.on('error', (error: any) => {
      window.clearInterval(writeCheckTimer);
      writeCheckTimer = undefined;
      writeStream.destroy(new Error(`destroy writeStream cause error`));
      if (this.status === TaskStatus.paused) return;
      this.setStatus(TaskStatus.error);
      let errMessage = 'SMH.getObject';
      if (error.code === 'ENOSPC') {
        error.code = 'DiskSpaceIsFull';
        errMessage = 'SMH.DiskSpaceIsFull';
      } else if (error.code === 'ENOENT') {
        error.code = 'localFileNotFound';
        errMessage = 'SMH.LocalFileNotFound';
      }
      logger.ui.error(`[Downloader] [${this.info.name}] downloadPart writeStream error`, error, this.status);
      this.emit('error', {
        ...error,
        interface: errMessage,
        downloadType: 'advanced',
      });
    });

    rs.on('aborted', (error: any) => {
      cleanAndReStart(error?.message || 'advanceDownload aborted');
    });
    rs.on('data', (chunk: any) => {
      writeDataTime = Date.now();
      onProgress(chunk.length);
    });
    // 使用函数防抖监测数据写入，若两秒内无数据接入，则触发进度条，使下载速度清零
    rs.on(
      'data',
      _.debounce(() => {
        onProgress(0);
      }, 2000),
    );

    writeStream.on('finish', () => {
      this.streamGroup && this.streamGroup?.destroyStreamByBeanId(currentStreamBeanId);
      window.clearInterval(writeCheckTimer);
      writeStream.close();
      if (timeoutFinish) {
        logger.ui.info(`[Downloader] [${this.info.name}] downloadPart finish have error`);
        return;
      }
      logger.ui.info(`[Downloader] [${this.info.name}] downloadPart finish, part = ${part.PartNumber}`);
      done(null, res);
    });
    this.once('status_changed', val => {
      // 若下载任务被 取消|删除|暂停，及时关闭写入
      if ([TaskStatus.canceled, TaskStatus.error, TaskStatus.paused].includes(val)) {
        if (writeCheckTimer) {
          window.clearInterval(writeCheckTimer);
          writeCheckTimer = undefined;
        }
        timeoutFinish = false;
        if (!this.streamGroup) {
          if (writeStream) writeStream.close();
        } else {
          this.streamGroup.destroyAllStreams();
        }
      }
    });
  }

  /**
   * @description 下载单独分片
   * @private
   */
  private async downloadPart(
    part: PartsInfo,
    options: {
      onProgress: (loaded: number) => void;
      resetSize: () => void;
    },
  ) {
    // 整理分块请求参数
    const headers: any = {};
    let url = this.info.cosUrl;
    headers.range = `bytes=${part.Start}-${part.End}`;
    let shouldStopByCancel = false; // 下载已取消时，重试要停下来
    let retryTimes = -1; // 保存重试次数
    const asyncTimeStamp = this.startTimeStamp;
    return async.retry(
      {
        times: DownloadConfig.ChunkRetryTimes,
        interval: 1000, // 重试间隔 1s
      },
      (done: any) => {
        retryTimes++;
        if (!this.isRunning() || shouldStopByCancel || asyncTimeStamp !== this.startTimeStamp) {
          done(new Error('canceled'));
          return;
        }
        const startTime = Date.now();
        this.cosRequest({
          url,
          method: 'GET',
          headers,
          timeout: DownloadConfig.RequestTimeout,
          responseType: 'stream',
          cancelToken: this.cancelRequestSource.token,
        })
          .then(async res => {
            await this.getDownloadPartData(res, done, part, {
              ...options,
              retryTimes,
              startTime,
            });
          })
          .catch(async error => {
            logger.ui.error(
              `[Downloader] [${this.info.name}] downloadPart error`,
              part.PartNumber,
              error.message,
              this.status,
            );

            if (error.message === 'canceled') {
              shouldStopByCancel = true;
            }
            let isExpired = false;
            if (error.status === 403) {
              isExpired =
                error.message === 'Request has expired' ||
                // 公有云判断超时
                error.code === 'RequestTimeTooSkewed' ||
                // 私有云超时判断
                isExpiredInPrivate(url as string, error.headers?.date); // 私有云超时判断
            }

            if (isExpired) {
              // 因为链接超时导致的错误需要重新更新一下链接地址
              await this.getDownloadCosUrl(isExpired);
              url = this.info.cosUrl;
            }
            done({
              ...error,
              interface: 'SMH.downloadMultiPart',
            });
          });
      },
    );
  }

  /**
   * @description 本次下载结束
   * @private
   * @memberof Downloader
   */
  private async finishDownload() {
    // 简单下载完成下载时status应该为running，高级下载完成下载时status应该为preparing
    const cannotFinish = () => {
      return (
        (this.isSimpleDownload() && this.status !== TaskStatus.running) ||
        (!this.isSimpleDownload() && this.status !== TaskStatus.preparing)
      );
    };

    if (cannotFinish()) return;
    const { savePath, savePathTmp } = this.info;
    if (savePathTmp && savePath && fs.existsSync(savePathTmp)) {
      await this.getDownloadFileInfo();
      if (cannotFinish()) return;
      // 最后取一次线上文件的 crc64 与下载前的crc64做对比，校验远端文件是否已经发生了修改
      const { size, crc64, newCrc64 } = this.info;
      if (crc64 !== newCrc64) {
        return Promise.reject({
          interface: 'SMH.remoteFileHasChanged',
          code: 'remoteFileHasChanged',
        });
      }
      try {
        const stat = fs.statSync(savePathTmp);
        if (stat && stat.size !== size) {
          return Promise.reject({
            interface: 'SMH.download_check_size_not_match',
            code: 'download_check_size_not_match',
          });
        }
      } catch (e: any) {
        return Promise.reject({
          interface: 'SMH.finishDownload_statSync_error',
          ...e,
        });
      }
      fs.renameSync(savePathTmp, savePath);
      return;
    }
    return Promise.reject({
      interface: 'SMH.localFileNotFound',
      code: 'localFileNotFound',
    });
  }

  isSimpleDownload() {
    const { size } = this.info;
    return size < DownloadConfig.PartDownloadFileSize;
  }

  /**
   * @description 开始下载
   * @memberof Downloader
   */
  async start(restart: boolean): Promise<void> {
    // 开始处理文件IO，统一设置为preparing
    this.setStatus(TaskStatus.preparing);
    this.info.restart = restart;
    try {
      await this.getDownloadCosUrl(true);
    } catch (error: MsgError) {
      this.setStatus(TaskStatus.error);
      if (error?.code === 'FileContentNotAvailable') {
        error.code = 'RiskFileCannotDownload';
      }
      this.emit('error', { ...error });
      return;
    }
    if (this.isSimpleDownload()) {
      this.simpleDownload();
    } else {
      this.advanceDownload();
    }
  }

  /**
   * @description 获取cosUrl下载信息
   * @param {boolean} [forceRefresh=true] 是否要强制更新缓存 or 仅在url失效时更新缓存
   * @return {*}
   * @memberof Downloader
   */
  async getDownloadCosUrl(forceRefresh = false) {
    const refresh = async () => {
      const { data } = await smh.commonAPI.getDownloadCosUrl({
        filePath: this.info.filePath,
        spaceId: this.info.spaceId,
        historyId: this.info.historyId,
      });
      const { crc64, cosUrl, availableCosUrls, cosUrlExpiration, contentType } = data;
      this.info.cosUrl = cosUrl;
      // cosUrl备用域名
      if (availableCosUrls) {
        this.info.availableCosUrls = availableCosUrls;
      }
      this.info.newCrc64 = crc64;
      this.info.cosUrlExpiration = cosUrlExpiration;
      this.info.contentType = contentType;
    };
    try {
      if (forceRefresh) {
        await refresh();
      } else if (this.info.cosUrlExpiration) {
        const currTime = systemClock.now();
        const expireTime = dayjs(this.info.cosUrlExpiration).valueOf();
        const expireOffset = 5 * 3600;
        if (currTime + expireOffset > expireTime) {
          await refresh();
          logger.ui.info('[Downloader] 当前 url 签名超时，进行刷新。');
        }
      } else {
        await refresh();
      }
    } catch (error: MsgError) {
      return Promise.reject(error);
    }
  }

  /** @description 获取文件信息(不包含cosUrl),取最新的crc64,用于下载完成比对
   */
  async getDownloadFileInfo() {
    try {
      const { data } = await smh.commonAPI.getDownloadFileInfo({
        filePath: this.info.filePath,
        spaceId: this.info.spaceId,
        historyId: this.info.historyId,
      });
      const { crc64 } = data;
      this.info.newCrc64 = crc64;
    } catch (error) {
      return Promise.reject(error);
    }
    // const refresh = async () => {
    //   const { data } = await smh.commonAPI.getDownloadFileInfo({
    //     filePath: this.info.filePath,
    //     spaceId: this.info.spaceId,
    //     historyId: this.info.historyId,
    //   });
    //   const { crc64, cosUrl, cosUrlExpiration, contentType } = data;
    //   this.info.cosUrl = cosUrl;
    //   this.info.newCrc64 = crc64;
    //   this.info.cosUrlExpiration = cosUrlExpiration;
    //   this.info.contentType = contentType;
    // };
    // try {
    //   if (forceRefresh) {
    //     await refresh();
    //   } else if (this.info.cosUrlExpiration) {
    //     const currTime = systemClock.now();
    //     const expireTime = dayjs(this.info.cosUrlExpiration).valueOf();
    //     const expireOffset = 5 * 3600;
    //     if (currTime + expireOffset > expireTime) {
    //       await refresh();
    //       logger.ui.info('[Downloader] 当前 url 签名超时，进行刷新。');
    //     }
    //   } else {
    //     await refresh();
    //   }
    // } catch (error: MsgError) {
    //   return Promise.reject(error);
    // }
  }

  /**
   * @description 暂停下载
   * @memberof Downloader
   */
  pause(): void {
    this.setStatus(TaskStatus.paused);
    this.startTimeStamp = Date.now();
  }

  /**
   * @description 正在下载中
   * @private
   * @memberof Downloader
   */
  isRunning(): boolean {
    return (
      this.status === TaskStatus.running || this.status === TaskStatus.waiting || this.status === TaskStatus.preparing
    );
  }

  /**
   * @description 完成状态判断
   * @private
   * @memberof Downloader
   */
  isFinished(): boolean {
    return (
      this.status === TaskStatus.success || this.status === TaskStatus.error || this.status === TaskStatus.canceled
    );
  }

  /**
   * @description 取消下载正在下载中的文件
   * @memberof Downloader
   */
  cancel(): void {
    this.setStatus(TaskStatus.canceled);
    this.startTimeStamp = Date.now();
  }
}
